import { fetchAuthSession } from "aws-amplify/auth";
import { client } from "../../amplify-client";

export const REPORT_REASON_OPTIONS = [
  { code: "HARASSMENT", label: "Harassment or bullying" },
  { code: "HATE_SPEECH", label: "Hate speech or discrimination" },
  { code: "SPAM", label: "Spam or scam behavior" },
  { code: "THREAT", label: "Threats or violent content" },
  { code: "SEXUAL_CONTENT", label: "Sexual or explicit content" },
  { code: "IMPERSONATION", label: "Impersonation" },
  { code: "OTHER", label: "Other" },
];

export const REPORT_STATUS_OPTIONS = ["OPEN", "IN_REVIEW", "RESOLVED", "DISMISSED"];
export const ACCESS_STATUS_OPTIONS = ["ACTIVE", "DISABLED", "SUSPENDED"];
export const STAFF_ROLE_OPTIONS = ["SUPERADMIN", "ADMIN", "MODERATOR", "MARKETING"];
const DEFAULT_ADMIN_GROUPS = ["Admins", "SuperAdmins"];
const MARKETING_CAMPAIGN_INPUT_KEYS = Object.freeze([
  "campaignId",
  "campaignName",
  "templateId",
  "channel",
  "region",
  "demographics",
  "activityLevel",
  "onboardingStage",
  "interests",
  "minimumAudience",
  "scheduleAt",
  "timezone",
  "throttlePerMinute",
  "quietHourStart",
  "quietHourEnd",
  "abEnabled",
  "subjectA",
  "subjectB",
  "contentA",
  "contentB",
]);

function throwIfErrors(response) {
  if (response?.errors?.length) {
    throw new Error(response.errors.map((err) => err.message).join(", "));
  }
}

function toMarketingCampaignInput(campaign = {}) {
  return MARKETING_CAMPAIGN_INPUT_KEYS.reduce((acc, key) => {
    if (campaign?.[key] !== undefined) {
      acc[key] = campaign[key];
    }
    return acc;
  }, {});
}

function toNormalizedGroups(rawGroups) {
  if (Array.isArray(rawGroups)) {
    return rawGroups.map((entry) => String(entry || "").trim()).filter(Boolean);
  }

  const raw = String(rawGroups || "").trim();
  if (!raw) return [];

  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry || "").trim()).filter(Boolean);
      }
    } catch {
      // Fall back to comma parsing.
    }
  }

  return raw.split(",").map((entry) => entry.trim()).filter(Boolean);
}

export async function isCurrentUserAdmin(adminGroups = DEFAULT_ADMIN_GROUPS) {
  const session = await fetchAuthSession();
  const claims = session?.tokens?.idToken?.payload || {};
  const groups = new Set(toNormalizedGroups(claims["cognito:groups"] ?? claims.groups));
  return adminGroups.some((group) => groups.has(group));
}

export async function getConsoleAccessContext() {
  const query = client?.queries?.getConsoleAccessContext;
  if (typeof query !== "function") {
    throw new Error("getConsoleAccessContext query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({});
  throwIfErrors(response);
  return response?.data || null;
}

export async function syncMyStaffMfaPolicy() {
  const mutation = client?.mutations?.syncMyStaffMfaPolicy;
  if (typeof mutation !== "function") {
    throw new Error("syncMyStaffMfaPolicy mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({});
  throwIfErrors(response);
  return response?.data || null;
}

export async function getMarketingWorkspaceData(filters = {}) {
  const query = client?.queries?.getMarketingWorkspaceData;
  if (typeof query !== "function") {
    throw new Error("getMarketingWorkspaceData query is unavailable. Deploy backend schema updates.");
  }

  const response = await query(filters || {});
  throwIfErrors(response);
  return response?.data || null;
}

export async function saveMarketingCampaign({ campaign, action = "DRAFT" } = {}) {
  const mutation = client?.mutations?.saveMarketingCampaign;
  if (typeof mutation !== "function") {
    throw new Error("saveMarketingCampaign mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({
    campaign: toMarketingCampaignInput(campaign),
    action,
  });
  throwIfErrors(response);
  return response?.data || null;
}

export async function setMarketingAutomationRule({
  ruleId,
  enabled,
  label,
  windowDays,
  channel,
} = {}) {
  const mutation = client?.mutations?.setMarketingAutomationRule;
  if (typeof mutation !== "function") {
    throw new Error("setMarketingAutomationRule mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({
    ruleId,
    enabled,
    label,
    windowDays,
    channel,
  });
  throwIfErrors(response);
  return response?.data || null;
}

export async function listStaffUsers({ limit = 50 } = {}) {
  const query = client?.queries?.listStaffUsers;
  if (typeof query !== "function") {
    throw new Error("listStaffUsers query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({ limit });
  throwIfErrors(response);
  return Array.isArray(response?.data) ? response.data : [];
}

export async function searchPlatformUsers({ query, limit = 10 } = {}) {
  const searchQuery = client?.queries?.searchPlatformUsers;
  if (typeof searchQuery !== "function") {
    throw new Error("searchPlatformUsers query is unavailable. Deploy backend schema updates.");
  }

  const response = await searchQuery({ query, limit });
  throwIfErrors(response);
  return Array.isArray(response?.data) ? response.data : [];
}

export async function listGovernanceActions({ limit = 50 } = {}) {
  const query = client?.queries?.listGovernanceActions;
  if (typeof query !== "function") {
    throw new Error("listGovernanceActions query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({ limit });
  throwIfErrors(response);
  return Array.isArray(response?.data) ? response.data : [];
}

export async function listModerationActions({ limit = 80, targetUserId, reportId } = {}) {
  const query = client?.queries?.listModerationActions;
  if (typeof query !== "function") {
    throw new Error("listModerationActions query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({ limit, targetUserId, reportId });
  throwIfErrors(response);
  return Array.isArray(response?.data) ? response.data : [];
}

export async function setUserStaffRole({ userId, role, enabled, reason }) {
  const mutation = client?.mutations?.setUserStaffRole;
  if (typeof mutation !== "function") {
    throw new Error("setUserStaffRole mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({ userId, role, enabled, reason });
  throwIfErrors(response);
  return response?.data || null;
}

export async function recordModerationAction({ targetUserId, actionType, notes, reportId }) {
  const mutation = client?.mutations?.recordModerationAction;
  if (typeof mutation !== "function") {
    throw new Error("recordModerationAction mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({ targetUserId, actionType, notes, reportId });
  throwIfErrors(response);
  return response?.data || null;
}

export async function setUserStaffRoleBreakGlass({ userId, role, enabled, reason, incidentTicket }) {
  const mutation = client?.mutations?.setUserStaffRoleBreakGlass;
  if (typeof mutation !== "function") {
    throw new Error("setUserStaffRoleBreakGlass mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({ userId, role, enabled, reason, incidentTicket });
  throwIfErrors(response);
  return response?.data || null;
}

export async function createUserReport({
  targetType,
  targetUserId,
  chatPk,
  messageSk,
  messageId,
  reasonCode,
  reasonText,
  sourceSurface,
}) {
  const mutation = client?.mutations?.createUserReport;
  if (typeof mutation !== "function") {
    throw new Error("createUserReport mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({
    targetType,
    targetUserId,
    chatPk,
    messageSk,
    messageId,
    reasonCode,
    reasonText,
    sourceSurface,
  });
  throwIfErrors(response);
  return response?.data || null;
}

export async function listReportsForReview({ status, targetUserId, limit = 50 } = {}) {
  const query = client?.queries?.listReportsForReview;
  if (typeof query !== "function") {
    throw new Error("listReportsForReview query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({ status, targetUserId, limit });
  throwIfErrors(response);
  return Array.isArray(response?.data) ? response.data : [];
}

export async function getReportDetail(reportId) {
  const query = client?.queries?.getReportDetail;
  if (typeof query !== "function") {
    throw new Error("getReportDetail query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({ reportId });
  throwIfErrors(response);
  return response?.data || null;
}

export async function updateReportStatus({ reportId, status, adminNotes }) {
  const mutation = client?.mutations?.updateReportStatus;
  if (typeof mutation !== "function") {
    throw new Error("updateReportStatus mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({ reportId, status, adminNotes });
  throwIfErrors(response);
  return response?.data || null;
}

export async function setUserAccess({
  userId,
  status,
  reasonCode,
  reasonText,
  disabledUntil,
  relatedReportId,
}) {
  const mutation = client?.mutations?.setUserAccess;
  if (typeof mutation !== "function") {
    throw new Error("setUserAccess mutation is unavailable. Deploy backend schema updates.");
  }

  const response = await mutation({
    userId,
    status,
    reasonCode,
    reasonText,
    disabledUntil,
    relatedReportId,
  });
  throwIfErrors(response);
  return response?.data || null;
}

export async function getMyAccessState() {
  const query = client?.queries?.getMyAccessState;
  if (typeof query !== "function") {
    throw new Error("getMyAccessState query is unavailable. Deploy backend schema updates.");
  }

  const response = await query({});
  throwIfErrors(response);
  return response?.data || null;
}
